import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../core/config/api';
import { useRealtimeInvalidation } from '../realtime/RealtimeProvider';

export interface RecruitmentRole {
  id: number;
  role_id: number;
  role_name: string;
  role_description: string | null;
  mission: string | null;
  ordre: number;
}

export interface RecruitmentState {
  enabled: boolean;
  message: string | null;
  roles: RecruitmentRole[];
}

/**
 * État public du recrutement (bannière d'accueil). Polling 30 s + rafraîchissement instantané
 * via la couche temps réel (`data:invalidate` domaine `recruitment`) pour les utilisateurs connectés.
 */
export function useRecruitmentStatus() {
  const [recruitment, setRecruitment] = useState<RecruitmentState | null>(null);

  const fetchState = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const res = await fetch(`${getApiBaseUrl(true)}/recruitment`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (data?.success && data?.data) {
        setRecruitment({
          enabled: data.data.enabled === true || data.data.enabled === 1,
          message: data.data.message ?? null,
          roles: Array.isArray(data.data.roles) ? data.data.roles : [],
        });
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.debug('Recruitment status check failed:', error);
      }
    }
  }, []);

  useEffect(() => {
    void fetchState();
    const id = window.setInterval(fetchState, 30000);
    return () => window.clearInterval(id);
  }, [fetchState]);

  useRealtimeInvalidation('recruitment', () => {
    void fetchState();
  });

  return { recruitment, refresh: fetchState };
}

export default useRecruitmentStatus;
